/** 指南选择器(评审管理 / AI 评审左侧共用).
 * 原来两页各写死 page_size:50 且无搜索:超过 50 条的指南永远进不了列表
 * (排序 created_at desc,老指南被挤出前 50 即"消失")。改为 q 标题搜索 + 滚动分页加载。
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import { Card, Empty, Input, Space, Spin, Tag } from "antd";
import type { ReactNode, UIEvent } from "react";
import { useState } from "react";
import { listGuides, STATUS_LABELS, type GuideListItem } from "@/features/guides/api";

const PAGE_SIZE = 20;

/** 左侧指南列表的 react-query key 前缀;提交/删除评审单后可 invalidate 刷新标签 */
export const GUIDE_PICKER_KEY = "admin-guide-picker";

export function GuidePicker({
  title,
  extra,
  selectedId,
  onSelect,
}: {
  title: ReactNode;
  extra?: ReactNode;
  selectedId: string | null;
  onSelect: (g: GuideListItem) => void;
}) {
  const [q, setQ] = useState("");

  const guidesQ = useInfiniteQuery({
    queryKey: [GUIDE_PICKER_KEY, q],
    queryFn: ({ pageParam }) =>
      listGuides({ page: pageParam, page_size: PAGE_SIZE, q: q || undefined }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.page_size < last.total ? last.page + 1 : undefined,
  });

  const items = guidesQ.data?.pages.flatMap((p) => p.items) ?? [];
  const total = guidesQ.data?.pages[0]?.total ?? 0;

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      guidesQ.hasNextPage && !guidesQ.isFetchingNextPage &&
      el.scrollTop + el.clientHeight >= el.scrollHeight - 24
    ) {
      guidesQ.fetchNextPage();
    }
  };

  return (
    <Card title={title} extra={extra} size="small" styles={{ body: { padding: 0 } }}>
      <div style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
        <Input.Search
          allowClear
          placeholder="按标题搜索(中/英文)"
          onSearch={(v) => setQ(v.trim())}
          onChange={(e) => {
            if (e.target.value === "") setQ("");
          }}
        />
      </div>
      <div style={{ maxHeight: "70vh", overflowY: "auto" }} onScroll={onScroll}>
        {guidesQ.isLoading && (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spin />
          </div>
        )}
        {items.map((g) => (
          <div
            key={g.id}
            onClick={() => onSelect(g)}
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid #f0f0f0",
              cursor: "pointer",
              background: selectedId === g.id ? "#f0f5ff" : undefined,
            }}
          >
            <div style={{ fontWeight: 500, marginBottom: 4 }}>{g.title}</div>
            <Space size={6} style={{ fontSize: 12, color: "#888" }}>
              <Tag>{g.reg_no}</Tag>
              <Tag>{STATUS_LABELS[g.status]}</Tag>
              {g.score != null && <Tag color="blue">{g.score}分</Tag>}
              {g.level && <Tag color="gold">{g.level}</Tag>}
            </Space>
          </div>
        ))}
        {!guidesQ.isLoading && items.length === 0 && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
        {guidesQ.isFetchingNextPage && (
          <div style={{ padding: 8, textAlign: "center" }}>
            <Spin size="small" />
          </div>
        )}
        {total > 0 && (
          <div style={{ padding: 8, textAlign: "center", fontSize: 12, color: "#999" }}>
            已加载 {items.length} / {total}
          </div>
        )}
      </div>
    </Card>
  );
}
